"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DragSlider = exports.useDragSliderState = exports.DragSliderContext = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const react_dom_1 = require("react-dom");
const nano_theme_1 = require("nano-theme");
const context_1 = require("../../styles/context");
const context_2 = require("./context");
var context_3 = require("./context");
Object.defineProperty(exports, "DragSliderContext", { enumerable: true, get: function () { return context_3.DragSliderContext; } });
Object.defineProperty(exports, "useDragSliderState", { enumerable: true, get: function () { return context_3.useDragSliderState; } });
const wrapperClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    jc: 'center',
    ts: 'none',
    us: 'none',
    '-webkit-touch-callout': 'none',
    touchAction: 'none',
});
const overlayClass = (0, nano_theme_1.rule)({
    pos: 'fixed',
    top: 0,
    left: 0,
    w: '100vw',
    h: '100vh',
    pe: 'none',
    z: 2147483647,
});
const svgClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    top: 0,
    left: 0,
    w: '100%',
    h: '100%',
    ov: 'visible',
});
const tooltipClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    pd: '3px 8px',
    bdrad: '4px',
    fz: '12px',
    lh: '1',
    ff: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
    whiteSpace: 'nowrap',
    fw: 600,
    bxsh: '0 2px 8px rgba(0,0,0,.18)',
    transform: 'translate(-50%, -100%)',
});
const defaultFormat = (value) => {
    if (Number.isInteger(value))
        return String(value);
    return value.toFixed(2);
};
const snap = (v, step) => Math.round(v / step) * step;
const DragOverlay = ({ startX, startY, currentX, currentY, lineAxis, label, showStartDot = true, startDotSize = 7, }) => {
    const styles = (0, context_1.useStyles)();
    const accent = styles.col.get('accent', 'solid-1');
    const tooltipFg = styles.light ? '#fff' : styles.g(0.04);
    const tipX = lineAxis === 'y' ? startX : currentX;
    const tipY = lineAxis === 'x' ? startY : currentY;
    const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
    const tooltipX = Math.min(Math.max(tipX, 36), Math.max(36, viewportW - 36));
    const tooltipY = Math.max(tipY - 14, 22);
    return (React.createElement("div", { className: overlayClass },
        React.createElement("svg", { className: svgClass },
            React.createElement("defs", null,
                React.createElement("marker", { id: "json-joy-dragslider-arrow", viewBox: "0 0 10 10", refX: "9", refY: "5", markerWidth: "6", markerHeight: "6", orient: "auto-start-reverse" },
                    React.createElement("path", { d: "M 0 0 L 10 5 L 0 10 z", fill: accent }))),
            React.createElement("line", { x1: startX, y1: startY, x2: tipX, y2: tipY, stroke: accent, strokeWidth: 1.5, strokeDasharray: "3 4", markerEnd: "url(#json-joy-dragslider-arrow)" }),
            showStartDot && startDotSize > 0 && React.createElement("circle", { cx: startX, cy: startY, r: startDotSize / 2, fill: accent })),
        label !== undefined && !!label && (React.createElement("div", { className: tooltipClass, style: { left: tooltipX, top: tooltipY, background: accent, color: tooltipFg } }, label))));
};
const resolveLineAxis = (axis, lineAxis) => {
    if (lineAxis)
        return lineAxis;
    if (axis === 'x')
        return 'x';
    if (axis === 'y')
        return 'y';
    return 'free';
};
const DragSlider = (props) => {
    const { value, onChange, onStart, onEnd, min, max, step = 0, sensitivity = 1, axis = 'x', lineAxis, format = defaultFormat, hideTooltip, hideStartDot, startDotSize, cursor, disabled, className, style, children, } = props;
    const ref = React.useRef(null);
    const [drag, setDrag] = React.useState(null);
    const propsRef = React.useRef({
        onChange,
        onStart,
        onEnd,
        format,
        min,
        max,
        step,
        sensitivity,
        axis,
        lineAxis,
        value,
    });
    propsRef.current = {
        onChange,
        onStart,
        onEnd,
        format,
        min,
        max,
        step,
        sensitivity,
        axis,
        lineAxis,
        value,
    };
    const handlePointerDown = React.useCallback((e) => {
        if (disabled || e.button !== 0)
            return;
        const el = ref.current;
        if (!el)
            return;
        e.preventDefault();
        e.stopPropagation();
        const rect = el.getBoundingClientRect();
        const startX = rect.left + rect.width / 2;
        const startY = rect.top + rect.height / 2;
        const startValue = propsRef.current.value;
        let currentValue = startValue;
        try {
            el.setPointerCapture(e.pointerId);
        }
        catch { }
        propsRef.current.onStart?.(startValue);
        setDrag({ startX, startY, x: e.clientX, y: e.clientY, value: startValue });
        const apply = (clientX, clientY) => {
            const p = propsRef.current;
            const dx = clientX - startX;
            const dy = clientY - startY;
            const delta = p.axis === 'y' ? -dy : dx;
            let next = startValue + delta * p.sensitivity;
            if (p.step > 0)
                next = snap(next, p.step);
            if (typeof p.min === 'number')
                next = Math.max(p.min, next);
            if (typeof p.max === 'number')
                next = Math.min(p.max, next);
            if (next !== currentValue) {
                currentValue = next;
                p.onChange(next);
            }
            setDrag((prev) => (prev ? { ...prev, x: clientX, y: clientY, value: next } : prev));
        };
        const handleMove = (ev) => {
            apply(ev.clientX, ev.clientY);
        };
        const cleanup = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
            window.removeEventListener('keydown', handleKey);
            try {
                el.releasePointerCapture(e.pointerId);
            }
            catch { }
            setDrag(null);
        };
        const handleUp = () => {
            propsRef.current.onEnd?.(currentValue, false);
            cleanup();
        };
        const handleKey = (ev) => {
            if (ev.key === 'Escape') {
                ev.preventDefault();
                ev.stopPropagation();
                propsRef.current.onChange(startValue);
                propsRef.current.onEnd?.(startValue, true);
                cleanup();
            }
        };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
        window.addEventListener('keydown', handleKey);
    }, [disabled]);
    const resolvedCursor = disabled
        ? 'default'
        : (cursor ?? (axis === 'y' ? 'ns-resize' : axis === 'both' ? 'move' : 'ew-resize'));
    const isDragging = !!drag;
    const contextValue = React.useMemo(() => ({ dragging: isDragging, axis }), [isDragging, axis]);
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { ref: ref, className: wrapperClass + (className ? ` ${className}` : ''), style: { cursor: resolvedCursor, ...style }, onPointerDown: handlePointerDown },
            React.createElement(context_2.DragSliderContext.Provider, { value: contextValue }, children)),
        drag &&
            typeof document !== 'undefined' &&
            (0, react_dom_1.createPortal)(React.createElement(DragOverlay, { startX: drag.startX, startY: drag.startY, currentX: drag.x, currentY: drag.y, lineAxis: resolveLineAxis(axis, lineAxis), label: hideTooltip ? undefined : format(drag.value), showStartDot: !hideStartDot, startDotSize: startDotSize }), document.body)));
};
exports.DragSlider = DragSlider;
